import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { fetchArchdeaconryBySlug } from '@/services/api';
import {
    Church,
    Users,
    MapPin,
    ChevronRight,
    ArrowLeft,
    Info,
    ShieldCheck,
    Building2
} from 'lucide-react';
import ImageHelper from '../lib/imageHelper';
import SeoHead from '@/components/Seo';
import { motion } from 'framer-motion';

interface Clergy {
    id: number;
    name: string;
    title: string;
    slug: string;
    photo?: string;
}

interface Parish {
    id: number;
    name: string;
    slug: string;
    location: string;
}

interface Archdeaconry {
    id: number;
    name: string;
    slug: string;
    parishes: Parish[];
    priests: Clergy[];
    description: string;
    current_archdeacon?: Clergy;
    current_supervisor?: Clergy;
}

const getInitials = (name: string) =>
    name?.split(' ').map((n) => n[0]).join('').toUpperCase();

const ArchdeaconryProfile = () => {
    const { slug } = useParams<{ slug: string }>();
    const { toast } = useToast();
    const [archdeaconry, setArchdeaconry] = useState<Archdeaconry | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadArchdeaconry = async () => {
            try {
                const response = await fetchArchdeaconryBySlug(slug!);
                setArchdeaconry(response.data);
            } catch (error) {
                toast({
                    title: 'Error',
                    description: 'Failed to fetch archdeaconry details.',
                    variant: 'destructive',
                });
            } finally {
                setLoading(false);
            }
        };
        loadArchdeaconry();
    }, [slug, toast]);

    // --- SEO BLOCK ---
    const seoContent = (
        <SeoHead
            title={archdeaconry ? `${archdeaconry.name} | Archdeaconries` : "Loading Archdeaconry..."}
            description={archdeaconry?.description?.substring(0, 160) || "Explore the parishes and clergy of the Ibadan South Diocese."}
            ogImage={archdeaconry?.current_archdeacon?.photo}
        />
    );

    if (loading) return (
        <>
            {seoContent}
            <div className="min-h-screen flex items-center justify-center bg-[#fcfaf7]">
                <div className="flex flex-col items-center gap-4">
                    <div className="h-12 w-12 border-4 border-amber-600/20 border-t-amber-600 rounded-full animate-spin"></div>
                    <p className="text-slate-400 font-serif italic">Accessing Records...</p>
                </div>
            </div>
        </>
    );

    if (!archdeaconry) return (
        <div className="p-20 text-center text-red-500 font-serif text-xl bg-[#fcfaf7] min-h-screen">
            Archdeaconry not found.
        </div>
    );

    const leader = archdeaconry.current_archdeacon || archdeaconry.current_supervisor;
    const leaderRole = archdeaconry.current_archdeacon ? 'Archdeacon' : 'Supervisor';

    return (
        <div className="min-h-screen bg-[#fcfaf7] pb-20">
            {seoContent}

            {/* Header / Hero */}
            <div className="bg-slate-900 text-white pt-24 pb-40 px-6 relative overflow-hidden">
                <div className="absolute inset-0 opacity-5 pointer-events-none">
                    <Building2 className="absolute -right-20 -bottom-20 h-96 w-96 rotate-12" />
                </div>

                <div className="max-w-6xl mx-auto relative z-10">
                    <Link to="/archdeaconries" className="inline-flex items-center text-amber-400/80 hover:text-amber-400 mb-8 transition-colors group text-xs font-black uppercase tracking-widest">
                        <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                        Return to Registry
                    </Link>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-5xl md:text-7xl font-serif font-bold mb-6"
                    >
                        {archdeaconry.name}
                    </motion.h1>
                    <div className="flex items-center gap-3 text-slate-400 bg-white/5 w-fit px-4 py-2 rounded-full backdrop-blur-sm border border-white/10">
                        <MapPin className="h-4 w-4 text-amber-500" />
                        <span className="uppercase tracking-[0.3em] text-[10px] font-black">Ibadan South Diocese</span>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-6 -mt-24 relative z-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                    {/* LEFT COLUMN: Leader & Description */}
                    <div className="lg:col-span-4 space-y-6">
                        {leader && (
                            <Card className="border-none shadow-2xl rounded-[2.5rem] overflow-hidden bg-white ring-1 ring-slate-100">
                                <div className="bg-slate-900 p-5 flex items-center justify-between text-white">
                                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-400">{leaderRole}</span>
                                    <ShieldCheck className="h-4 w-4 text-amber-500" />
                                </div>
                                <CardContent className="p-10 text-center">
                                    <div className="mx-auto w-32 h-32 mb-6 ring-8 ring-slate-50 rounded-[2rem] overflow-hidden shadow-inner bg-slate-50 flex items-center justify-center transition-transform hover:scale-105 duration-500">
                                        {leader.photo ? (
                                            <ImageHelper src={leader.photo} alt={leader.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-3xl font-serif font-bold text-slate-300">{getInitials(leader.name)}</span>
                                        )}
                                    </div>
                                    <h2 className="text-2xl font-serif font-bold text-slate-900 leading-tight mb-4">
                                        {leader.title} {leader.name}
                                    </h2>
                                    <Link
                                        to={`/clergy/${leader.slug}`}
                                        className="inline-flex items-center gap-2 px-6 py-2 bg-amber-50 text-amber-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-amber-600 hover:text-white transition-all"
                                    >
                                        Profile <ChevronRight className="h-3 w-3" />
                                    </Link>
                                </CardContent>
                            </Card>
                        )}

                        <Card className="border-none shadow-sm rounded-[2.5rem] bg-white/60 backdrop-blur-md border border-white">
                            <CardContent className="p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="h-2 w-2 rounded-full bg-amber-500" />
                                    <h3 className="font-black uppercase text-[10px] tracking-[0.2em] text-slate-400">Territory Profile</h3>
                                </div>
                                <p className="text-slate-600 text-base leading-relaxed font-serif italic">
                                    {archdeaconry.description || "The history and administrative profile of this Archdeaconry is currently being updated by the Diocese."}
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* RIGHT COLUMN: Lists */}
                    <div className="lg:col-span-8 space-y-8">

                        {/* Parishes Section */}
                        <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
                            <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
                                <div className="flex items-center gap-4">
                                    <div className="bg-slate-900 p-2.5 rounded-2xl text-amber-500 shadow-lg">
                                        <Church className="h-5 w-5" />
                                    </div>
                                    <h3 className="text-xl font-serif font-bold text-slate-900">Constituent Parishes</h3>
                                </div>
                                <span className="bg-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest text-slate-500 shadow-sm ring-1 ring-slate-100">
                                    {archdeaconry.parishes.length} Stations
                                </span>
                            </div>

                            <div className="p-4">
                                {archdeaconry.parishes.length === 0 ? (
                                    <div className="p-16 text-center text-slate-300 font-serif italic">No parishes listed in this jurisdiction.</div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {archdeaconry.parishes.map((parish) => (
                                            <Link
                                                key={parish.id}
                                                to={`/parishes/${parish.slug}`}
                                                className="flex items-center justify-between p-5 rounded-3xl bg-slate-50/50 hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 transition-all group ring-1 ring-transparent hover:ring-slate-100"
                                            >
                                                <div className="flex flex-col">
                                                    <span className="font-serif font-bold text-lg text-slate-900 group-hover:text-amber-700 transition-colors">{parish.name}</span>
                                                    <span className="text-[10px] text-slate-400 flex items-center mt-2 font-black uppercase tracking-widest">
                                                        <MapPin className="h-3 w-3 mr-1 text-amber-500/50" /> {parish.location}
                                                    </span>
                                                </div>
                                                <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-sm">
                                                    <ChevronRight className="h-4 w-4 text-amber-600" />
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Clergy Section */}
                        <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
                            <div className="p-8 border-b border-slate-50 flex items-center gap-4 bg-slate-50/30">
                                <div className="bg-amber-100 p-2.5 rounded-2xl text-amber-700">
                                    <Users className="h-5 w-5" />
                                </div>
                                <h3 className="text-xl font-serif font-bold text-slate-900">Assigned Clergy</h3>
                            </div>

                            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {archdeaconry.priests.length === 0 ? (
                                    <div className="col-span-full p-16 text-center text-slate-300 font-serif italic">No clergy currently assigned.</div>
                                ) : (
                                    archdeaconry.priests.map((clergy) => (
                                        <Link
                                            key={clergy.id}
                                            to={`/clergy/${clergy.slug}`}
                                            className="bg-white border border-slate-100 p-4 rounded-3xl flex items-center gap-4 hover:shadow-xl hover:shadow-amber-900/5 transition-all group"
                                        >
                                            <div className="h-12 w-12 shrink-0 rounded-2xl overflow-hidden bg-slate-50 ring-2 ring-slate-50 group-hover:ring-amber-100 transition-all">
                                                {clergy.photo ? (
                                                    <ImageHelper src={clergy.photo} alt={clergy.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-[10px] font-black text-slate-300">
                                                        {getInitials(clergy.name)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[9px] font-black text-amber-600 uppercase tracking-widest mb-0.5">{clergy.title}</p>
                                                <p className="text-sm font-bold text-slate-800 truncate group-hover:text-amber-700 transition-colors">{clergy.name}</p>
                                            </div>
                                        </Link>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ArchdeaconryProfile;